import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.resolvedLanguage ?? i18n.language;
  const isFrench = currentLanguage.startsWith('fr');

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === '/'
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const toggleLanguage = () => {
    const newLang = isFrench ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  // Simple flag components
  const FrenchFlag = () => (
    <svg width="16" height="12" viewBox="0 0 16 12" className="rounded-sm">
      <rect width="5.33" height="12" fill="#002395"/>
      <rect x="5.33" width="5.34" height="12" fill="#FFFFFF"/>
      <rect x="10.67" width="5.33" height="12" fill="#ED2939"/>
    </svg>
  );

  const BritishFlag = () => (
    <svg width="16" height="12" viewBox="0 0 16 12" className="rounded-sm">
      <rect width="16" height="12" fill="#012169"/>
      <path d="M0,0 L16,12 M16,0 L0,12" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M0,0 L16,12 M16,0 L0,12" stroke="#C8102E" strokeWidth="0.8" strokeLinecap="round"/>
      <rect x="6" y="0" width="4" height="12" fill="#FFFFFF"/>
      <rect x="0" y="4" width="16" height="4" fill="#FFFFFF"/>
      <rect x="6.5" y="0" width="3" height="12" fill="#C8102E"/>
      <rect x="0" y="4.5" width="16" height="3" fill="#C8102E"/>
    </svg>
  );

  const navigationItems = [
    { path: '/', label: t('nav.home') },
    { path: '/publications', label: t('nav.publications') },
    { path: '/talks', label: t('nav.talks') },
    { path: '/teaching', label: t('nav.teaching') },
    { path: '/software', label: t('nav.software') },
    { path: 'https://logb-research.github.io/', label: 'Blog', external: true }
  ];

  return (
    <nav className="sticky top-0 z-50 border-b border-stone-200/80 bg-[rgba(250,248,244,0.82)] backdrop-blur-md shadow-[0_12px_30px_rgba(31,41,55,0.05)]">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo/Name */}
          <Link 
            to="/" 
            className="font-display text-3xl font-semibold tracking-[0.04em] text-stone-800 transition-colors duration-300 hover:text-stone-600"
          >
            Oussama Zekri
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map(({ path, label, external }) => (
              external ? (
                <a
                  key={path}
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative px-3 py-2 text-sm font-medium text-stone-700 transition-all duration-300 hover:text-stone-500 flex items-center"
                >
                  {label}
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              ) : (
                <Link
                  key={path}
                  to={path}
                  className={`relative px-3 py-2 text-sm font-medium transition-all duration-300 hover:text-stone-500 ${
                    isActive(path) 
                      ? 'text-stone-800' 
                      : 'text-stone-700 hover:text-stone-500'
                  }`}
                >
                  {label}
                  {isActive(path) && (
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-stone-500 animate-slide-in"></span>
                  )}
                </Link>
              )
            ))}
            
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 rounded-full border border-stone-200 bg-white/75 px-3 py-2 text-sm font-medium text-stone-700 transition-colors duration-200 hover:bg-stone-100 hover:text-stone-500"
            >
              <div className="flex items-center space-x-1">
                <BritishFlag />
                <span className="text-xs text-gray-400">/</span>
                <FrenchFlag />
              </div>
              <span>EN/FR</span>
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            {/* Language Switcher for Mobile */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 rounded-full border border-stone-200 bg-white/75 px-2 py-1 text-sm font-medium text-stone-700 transition-colors duration-200 hover:bg-stone-100 hover:text-stone-500"
            >
              <BritishFlag />
              <span className="text-xs text-gray-400">/</span>
              <FrenchFlag />
              <span>EN/FR</span>
            </button>
            
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              className="rounded-lg p-2 transition-colors duration-200 hover:bg-stone-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-stone-200">
            <div className="flex flex-col space-y-2 pt-4">
              {navigationItems.map(({ path, label, external }) => (
                external ? (
                  <a
                    key={path}
                    href={path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="px-3 py-2 rounded-lg transition-all duration-200 text-stone-700 hover:bg-stone-50 hover:text-stone-500 flex items-center"
                  >
                    {label}
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                ) : (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-3 py-2 rounded-lg transition-all duration-200 ${
                      isActive(path)
                        ? 'bg-stone-100 text-stone-800 font-medium'
                        : 'text-stone-700 hover:bg-stone-50 hover:text-stone-500'
                    }`}
                  >
                    {label}
                  </Link>
                )
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 
